import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, MessageSquare, Settings, LogOut, Menu, X, Star, Shield, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

import DashboardOverview from './admin/DashboardOverview';
import SettingsManagement from './admin/SettingsManagement';
import GuidesManagement from './admin/GuidesManagement';
import ServicesManagement from './admin/ServicesManagement';
import ReviewsManagement from './admin/ReviewsManagement';
import LeadsManagement from './admin/LeadsManagement';
import UsersManagement from './admin/UsersManagement';
import BlogManagement from './admin/BlogManagement';

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center dark:bg-gray-900 dark:text-white">Loading...</div>;
  }

  if (!isAuthenticated()) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'ড্যাশবোর্ড', icon: LayoutDashboard },
    { path: '/admin/guides', label: 'গাইড', icon: FileText },
    { path: '/admin/services', label: 'সেবাদাতা', icon: Users },
    { path: '/admin/reviews', label: 'রিভিউ', icon: Star },
    { path: '/admin/blog', label: 'ব্লগ', icon: BookOpen },
    { path: '/admin/leads', label: 'লিড', icon: MessageSquare },
    { path: '/admin/users', label: 'ব্যবহারকারী', icon: Shield, masterOnly: true },
    { path: '/admin/settings', label: 'সেটিংস', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2E7D32] to-[#FBC02D] flex items-center justify-center">
            <span className="text-white font-bold text-sm">ভূ</span>
          </div>
          <span className="font-bold text-sm dark:text-white">Admin</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="dark:text-white">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex pt-14 lg:pt-0">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 pt-14 lg:pt-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 hidden lg:block">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2E7D32] to-[#FBC02D] flex items-center justify-center">
                <span className="text-white font-bold">ভূ</span>
              </div>
              <span className="font-bold text-lg dark:text-white">ভূমি Admin</span>
            </Link>
          </div>

          <nav className="px-4 py-6 lg:py-0">
            {navItems.filter(item => !item.masterOnly || user?.role === 'master_admin').map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors text-sm ${
                  isActive(item.path)
                    ? 'bg-[#E8F5E9] dark:bg-[#2E7D32]/20 text-[#2E7D32] font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full dark:border-gray-600 dark:text-gray-300" size="sm" onClick={handleLogout}>
              <LogOut size={14} className="mr-2" /> লগআউট
            </Button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 min-h-screen">
          <Routes>
            <Route path="/dashboard" element={<DashboardOverview />} />
            <Route path="/guides" element={<GuidesManagement />} />
            <Route path="/services" element={<ServicesManagement />} />
            <Route path="/reviews" element={<ReviewsManagement />} />
            <Route path="/blog" element={<BlogManagement />} />
            <Route path="/leads" element={<LeadsManagement />} />
            <Route path="/users" element={<UsersManagement />} />
            <Route path="/settings" element={<SettingsManagement />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
